import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { api } from 'src/common/utils/api';
import type { SnoozeReason } from 'src/notifications/copy/snoozeReasonCopy';
import type { Notification } from 'src/notifications/types/Notification';

type SnoozePayload = {
  id: string;
  days: number;
  reason: SnoozeReason;
  reasonText?: string;
};

export function useNotificationActions() {
  const queryClient = useQueryClient();

  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['notifications'] });
    dispatchAppEvent('NOTIFICATION_ACTIONED');
  };

  const markRead = useMutation<{ data: Notification }, Error, string>({
    mutationFn: id => api.post<Notification>(`notifications/${id}/read`),
    onSuccess,
  });

  const markUnread = useMutation<{ data: Notification }, Error, string>({
    mutationFn: id => api.post<Notification>(`notifications/${id}/unread`),
    onSuccess,
  });

  const dismiss = useMutation<{ data: Notification }, Error, string>({
    mutationFn: id => api.post<Notification>(`notifications/${id}/dismiss`),
    onSuccess,
  });

  const snooze = useMutation<{ data: Notification }, Error, SnoozePayload>({
    mutationFn: ({ id, days, reason, reasonText }) =>
      api.post<Notification>(`notifications/${id}/snooze`, { days, reason, reasonText }),
    onSuccess,
  });

  return { markRead, markUnread, dismiss, snooze };
}
